import {
  SourceEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  FeeSchedule,
  serviceCodeToFee,
  ServiceProviderHandler,
} from '@principle-theorem/principle-core';
import { ServiceCodeType } from '@principle-theorem/principle-core/interfaces';
import { getError, type WithRef } from '@principle-theorem/shared';
import { compact, groupBy, last, sortBy } from 'lodash';
import {
  BaseFeeScheduleDestination,
  IFeeScheduleJobData,
  IFeeScheduleMigrationData,
} from '../../../destination/entities/fee-schedules';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  D4WFeeSchedule,
  FeeScheduleSourceEntity,
} from '../../source/entities/fee-schedule';
import { D4WFeeScheduleMappingHandler } from '../mappings/fee-schedules';
import { D4WPracticeMappingHandler } from '../mappings/practices';

export class FeeScheduleDestinationEntity extends BaseFeeScheduleDestination<D4WFeeSchedule> {
  feeScheduleSourceEntity = new FeeScheduleSourceEntity();
  practiceCustomMapping = new D4WPracticeMappingHandler();
  feeScheduleCustomMapping = new D4WFeeScheduleMappingHandler();

  buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IFeeScheduleJobData<D4WFeeSchedule>
  ):
    | IFeeScheduleMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    try {
      if (
        data.feeScheduleRecord.record.status ===
        SourceEntityRecordStatus.Invalid
      ) {
        return this._buildErrorResponse(
          data.feeScheduleRecord.record,
          'Source fee schedule is invalid'
        );
      }

      const practiceId = data.feeScheduleRecord.data.practice_id;

      const practice = data.practices.find(
        (searchPractice) =>
          searchPractice.sourceIdentifier === practiceId.toString()
      );

      if (!practice || !practice.destinationIdentifier) {
        return this._buildErrorResponse(
          data.feeScheduleRecord.record,
          `Can't find practice with id ${practiceId}`
        );
      }

      const items = groupBy(
        data.feeScheduleRecord.data.items,
        (item) => `${item.item_id}`
      );

      const filteredItems = compact(
        Object.values(items).map((groupItems) =>
          last(sortBy(groupItems, (item) => item.end_date))
        )
      );

      return {
        isDefault: data.feeScheduleRecord.data.is_default,
        practiceRef: practice.destinationIdentifier,
        organisationRef: migration.configuration.organisation.ref,
        sourceFeeScheduleId: this.feeScheduleSourceEntity
          .getSourceRecordId(data.feeScheduleRecord.data)
          .toString(),
        feeSchedule: FeeSchedule.init({
          name: data.feeScheduleRecord.data.description,
          serviceCodeType: ServiceCodeType.ADA,
          serviceCodes: compact(
            filteredItems.map((item) => {
              const foundServiceCode = ServiceProviderHandler.findServiceCode(
                item.item_id
              );

              if (!foundServiceCode) {
                // eslint-disable-next-line no-console
                console.error(
                  `Can't find service code info for code ${item.item_id}`
                );
                return;
              }

              return serviceCodeToFee(foundServiceCode, item.price);
            })
          ),
        }),
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.feeScheduleRecord.record,
        `Error building migration data: ${getError(error)}`
      );
    }
  }
}
